import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FolderKanban, Archive } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLookups, useDirectory } from "@/lib/lookups";
import { PageHeader } from "@/components/PageHeader";
import { Modal, EmptyState, Progress, PageLoader, useToast } from "@/components/ui";
import { StatusBadge } from "@/components/badges";
import { Field, TextInput, TextArea, Select } from "@/components/fields";
import { money, pct, fmtDate } from "@/lib/format";
import type { ProjectSummary } from "@shared/types";

export function Projects() {
  const { can } = useAuth();
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["projects", showArchived],
    queryFn: () => api.get<{ projects: ProjectSummary[] }>(`/projects${showArchived ? "?archived=1" : ""}`),
  });

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Every project runs in parallel with its own schedule, Gantt and budget."
        actions={
          <>
            <button className="btn-ghost text-xs" onClick={() => setShowArchived((s) => !s)}>
              <Archive size={14} /> {showArchived ? "Hide" : "Show"} archived
            </button>
            {can("projects.create") && (
              <button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New project</button>
            )}
          </>
        }
      />

      {isLoading ? <PageLoader /> : !data?.projects.length ? (
        <EmptyState icon={FolderKanban} title="No projects yet"
          hint={can("projects.create") ? "Create your first project to start building its schedule." : "You have not been added to any projects yet."}
          action={can("projects.create") && <button className="btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New project</button>} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="card group p-4 transition hover:border-brand-400 hover:shadow-pop">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-serif text-lg font-semibold leading-tight group-hover:text-brand-600">{p.name}</p>
                  {p.code && <p className="text-[11px] uppercase tracking-wide text-ink-faint">{p.code}</p>}
                </div>
                <StatusBadge value={p.status} />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Progress value={p.overallPercent} className="flex-1" />
                <span className="text-xs font-semibold tabular-nums">{pct(p.overallPercent)}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-ink-soft">
                <span>Activities <b className="text-ink">{p.totalActivities}</b></span>
                <span>Delayed <b className={p.counts.delayed ? "text-st-delayed" : "text-ink"}>{p.counts.delayed || 0}</b></span>
                <span>Budget <b className="text-ink">{money(p.plannedBudget, p.currency)}</b></span>
                <span>Spent <b className="text-ink">{money(p.actualCost, p.currency)}</b></span>
                <span>Owner <b className="text-ink">{p.ownerName || "—"}</b></span>
                <span>Manager <b className="text-ink">{p.managerName || "—"}</b></span>
                <span>Starts <b className="text-ink">{fmtDate(p.startDate)}</b></span>
                <span>Ends <b className="text-ink">{fmtDate(p.endDate)}</b></span>
              </div>
              {p.isArchived && <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-ink-faint">Archived</p>}
            </Link>
          ))}
        </div>
      )}

      {creating && <CreateProjectModal onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateProjectModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const { byCategory } = useLookups();
  const users = useDirectory();
  const [f, setF] = useState({
    name: "", code: "", description: "", status: "Planning",
    ownerId: "", managerId: user?.id || "", startDate: "", endDate: "",
    plannedBudget: "", currency: "OMR", ganttWeeks: 70, useStandardPhases: true,
  });
  const [err, setErr] = useState("");

  const mut = useMutation({
    mutationFn: () => api.post<{ id: string }>("/projects", {
      ...f, plannedBudget: Number(f.plannedBudget) || 0, ganttWeeks: Number(f.ganttWeeks) || 70,
    }),
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.push("success", "Project created");
      onClose();
      window.location.href = `/projects/${id}/schedule`;
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed to create"),
  });

  const projectStatuses = byCategory("project_status");

  return (
    <Modal open onClose={onClose} title="New project" wide
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending || !f.name} onClick={() => { setErr(""); mut.mutate(); }}>Create project</button>
      </>}>
      {err && <div className="mb-3 rounded-lg bg-st-delayed/10 px-3 py-2 text-sm text-st-delayed">{err}</div>}
      <div className="grid gap-3.5 sm:grid-cols-2">
        <Field label="Project name" required><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Airport Clinic + Hotel Development" /></Field>
        <Field label="Code" hint="Short reference, auto-generated if blank"><TextInput value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="AIRPORT" /></Field>
        <div className="sm:col-span-2"><Field label="Description"><TextArea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
        <Field label="Status">
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {(projectStatuses.length ? projectStatuses : [{ value: "Planning", label: "Planning" }]).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </Field>
        <Field label="Currency"><TextInput value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} /></Field>
        <Field label="Project Owner (approves deliverables)">
          <Select value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })}>
            <option value="">— select —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
          </Select>
        </Field>
        <Field label="Project Manager">
          <Select value={f.managerId} onChange={(e) => setF({ ...f, managerId: e.target.value })}>
            {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
          </Select>
        </Field>
        <Field label="Start date"><TextInput type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
        <Field label="End date"><TextInput type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></Field>
        <Field label="Planned budget"><TextInput type="number" value={f.plannedBudget} onChange={(e) => setF({ ...f, plannedBudget: e.target.value })} /></Field>
        <Field label="Gantt timeline (weeks)"><TextInput type="number" value={f.ganttWeeks} onChange={(e) => setF({ ...f, ganttWeeks: Number(e.target.value) })} /></Field>
        <label className="col-span-full mt-1 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.useStandardPhases} onChange={(e) => setF({ ...f, useStandardPhases: e.target.checked })} />
          Pre-load the 14 standard clinic project phases
        </label>
      </div>
    </Modal>
  );
}
